import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Box, Container } from '@mui/system';
import { Button, FormControl, FormHelperText, IconButton, InputAdornment, Link, OutlinedInput, TextField, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useLocation } from "react-router-dom";
import useCheckUserToken from '../../../Helpers/Common';
import CircularLoader from '../../Common/CircularLoader';
import TokenExpired from './TokenExpired';
import PrimaryContent from '../../Utilities/PrimaryContent';

const AddUserLabel = ({ children }) => (
  <Typography
    variant='h2'
    sx={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', fontWeight: '500', color: '#000000', mb: 1 }}
  >
    {children}
  </Typography>
);

export default function ForgotPassword() {
  const [pageLoading, setPageLoading] = useState(false);
  const [forgotPasswordData, setForgotPasswordData] = useState({ clientId: '', userName: '' });
  const [resetPasswordData, setResetPasswordData] = useState({ newPassword: '', confirmNewPassword: '' });
  const [showPassword, setShowPassword] = useState({ newPassword: false, confirmNewPassword: false });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const forgotPasswordToken = queryParams.get("token");
  const { isExpired, isLoading } = useCheckUserToken(forgotPasswordToken);

  if (forgotPasswordToken) {
    if (isLoading) return <CircularLoader />;
    if (isExpired) return <TokenExpired />
  }

  const handleClickShowPassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForgotPasswordData({ ...forgotPasswordData, [name]: value });
    setResetPasswordData({ ...resetPasswordData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let newErrors = {};

    if (!forgotPasswordToken) {
      const { clientId, userName } = forgotPasswordData;
      if (!clientId) newErrors.clientId = true;
      if (!userName) newErrors.userName = true;
    } else {
      const { newPassword, confirmNewPassword } = resetPasswordData;
      if (newPassword !== confirmNewPassword) return toast.error('New Password and Confirm Password do not match');
      if (!newPassword) newErrors.newPassword = true;
      if (!confirmNewPassword) newErrors.confirmNewPassword = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please correct the errors.');
      return;
    }

    try {
      let response;
      setPageLoading(true);
      if (forgotPasswordToken) {
        response = await axios.put(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/user/reset-password/${forgotPasswordToken}`,
          {
            newPassword: resetPasswordData.newPassword,
            confirmNewPassword: resetPasswordData.confirmNewPassword,
          },
          { headers: { 'Content-Type': 'application/json' } }
        );
      } else {
        response = await axios.put(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/user/forgot-password`,
          {
            clientId: forgotPasswordData?.clientId,
            userName: forgotPasswordData?.userName,
          },
        );
      }
      toast.success(response?.data?.message || 'Request processed successfully');
      navigate('/login');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '35%', minHeight: '80vh', pt: 8, pb: 2, px: 5 }}>
      {!forgotPasswordToken &&
        <Box>
          <Box sx={{ display: 'flex', py: 1 }}>
            <Box sx={{ pr: '4px' }}>
              <img
                src="/images/leftArrow.png"
                alt="Icon"
                style={{ width: 24, height: 24, marginRight: 4, cursor: 'pointer' }}
                onClick={() => navigate('/login')}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Lato',
                  fontSize: '18px',
                  fontWeight: 700,
                }}
              >
                Back
              </Typography>
            </Box>
          </Box>
          <Typography
            variant='h1'
            sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '26px',
              fontWeight: '900',
              color: '#000',
              mb: 4,
            }}
          >
            Forgot Password
          </Typography>
          <form onSubmit={handleSubmit}>
            {['clientId', 'userName'].map((field) => (
              <Box key={field} sx={{ mb: 2 }}>
                <AddUserLabel>{field === 'clientId' ? 'Client ID' : 'User Name'}</AddUserLabel>
                <TextField
                  className="login-page-input"
                  name={field}
                  value={forgotPasswordData[field] || ""}
                  onChange={handleChange}
                  placeholder={field === 'clientId' ? 'Enter your Client ID' : 'Enter your Username'}
                  fullWidth
                  error={errors[field]}
                  helperText={errors[field] ? `${field === 'clientId' ? 'Client ID' : 'Username'} is required.` : ""}
                />
              </Box>
            ))}
            <Box sx={{ py: 2 }}>
              <Button
                type='submit'
                fullWidth
                disabled={pageLoading}
                variant='contained'
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontWeight: 700,
                  fontSize: '14px',
                  color: '#FFF',
                  py: 1,
                  background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                  textTransform: 'none',
                  '&.Mui-disabled': {
                    background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                    color: '#FFF',
                    opacity: '75%',
                  },
                }}
              >
                {pageLoading ? 'Loading Please wait...' : 'Forgot Password'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PrimaryContent sx={{ fontSize: '14px' }}>
                NOTE: If you forgot your Client ID or User Name,<br />
                please contact us at <Link href="mailto:admin@gbinternational.sg">admin@gbinternational.sg</Link>.
              </PrimaryContent>
            </Box>
          </form>
        </Box>
      }
      {forgotPasswordToken &&
        <Box>
          <Box sx={{ display: 'flex', py: 1 }}>
            <Box sx={{ pr: '4px' }}>
              <img
                src="/images/leftArrow.png"
                alt="Icon"
                style={{ width: 24, height: 24, marginRight: 4, cursor: 'pointer' }}
                onClick={() => navigate('/login')}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Lato',
                  fontSize: '18px',
                  fontWeight: 700,
                }}
              >
                Back
              </Typography>
            </Box>
          </Box>
          <Typography variant='h1' sx={{ fontFamily: 'Lato, sans-serif', fontSize: '26px', fontWeight: '900', color: '#000', mb: 4 }}>
            Reset Password
          </Typography>
          <form onSubmit={handleSubmit}>
            {['newPassword', 'confirmNewPassword'].map((field) => (
              <Box key={field} sx={{ mb: 2 }}>
                <AddUserLabel>{field === 'oldPassword' ? 'Old Password' : field === 'newPassword' ? 'New Password' : 'Confirm Password'}</AddUserLabel>
                <FormControl fullWidth error={errors[field]} size='small'>
                  <OutlinedInput
                    name={field}
                    size='medium'
                    type={showPassword[field] ? 'text' : 'password'}
                    value={forgotPasswordData[field]}
                    onChange={handleChange}
                    placeholder={`Enter your ${field === 'oldPassword' ? 'old' : field === 'newPassword' ? 'new' : 'confirmed new'} password`}
                    endAdornment={
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() => handleClickShowPassword(field)}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword[field] ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {errors[field] && (
                    <FormHelperText>
                      {errors[field] === true ? `${field === 'oldPassword' ? 'Old' : field === 'newPassword' ? 'New' : 'Confirm'} password is required.` : errors[field]}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            ))}
            <Box sx={{ py: 2 }}>
              <Button
                type='submit'
                fullWidth
                variant='contained'
                sx={{ fontFamily: 'Lato, sans-serif', fontWeight: 700, fontSize: '14px', color: '#FFF', py: 1, background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)', textTransform: 'none' }}
              >
                Reset Password
              </Button>
            </Box>
          </form>
        </Box>
      }
    </Container>
  );
}
